import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Card, Button, Switch, Space, Tag, Modal,
  Form, Input, message, Popconfirm, Empty, Spin, Alert, Divider,
  Tooltip, Tabs, Badge, List,
} from 'antd';
import {
  UserOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined,
  CrownOutlined, ToolOutlined, SafetyCertificateOutlined,
  SyncOutlined, KeyOutlined, StopOutlined, GlobalOutlined, MailOutlined, TeamOutlined,
} from '@ant-design/icons';
import { API_BASE_URL, getAuthHeaders } from '../api';
import { useAuth } from '../contexts/AuthContext';
import ScrapeUrls from './ScrapeUrls';
import ContactSubmissionsAdminTab from './ContactSubmissionsAdminTab';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

interface UserRow {
  uid: string;
  email: string;
  is_admin: boolean;
  competition_setup: boolean;
}

interface ApiKey {
  id: number;
  owner_uid: string;
  owner_email: string;
  created_at: string;
}

interface AthleteAccountRow {
  uid: string;
  email: string;
  full_name: string | null;
  date_of_birth: string | null;
  invite_status: string | null;
  invited_at: string | null;
  club_id: number | null;
  club_name: string | null;
  athlete_id: number | null;
  is_tagged: boolean;
}

interface AthleteTagRequestRow {
  uid: string;
  email: string | null;
  full_name: string | null;
  date_of_birth: string | null;
  club_name: string | null;
  club_id: number | null;
  tag_request_status: string | null;
  tag_request_note: string | null;
  athlete_id: number | null;
}

// ΓöÇΓöÇΓöÇ Users Tab ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

const UsersTab: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [addForm] = Form.useForm();
  const [adding, setAdding] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, { headers });
      const text = await res.text();
      if (!res.ok) { setApiError(`${res.status}: ${text}`); return; }
      const data = JSON.parse(text);
      setUsers(data.users || []);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/sync-users`, { method: 'POST', headers });
      const text = await res.text();
      if (!res.ok) { message.error(`Sync failed (${res.status}): ${text}`); return; }
      const data = JSON.parse(text);
      message.success(`Synced ${data.synced} users from Firebase.`);
      loadUsers();
    } catch (e: any) {
      message.error(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateRole = async (uid: string, email: string, is_admin: boolean, competition_setup: boolean) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${uid}/roles`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, is_admin, competition_setup }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      message.success('Updated ΓÇö user must re-login to see changes.');
      loadUsers();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    }
  };

  const handleAddUser = async (values: any) => {
    setAdding(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          display_name: values.displayName,
          password: values.password,
          is_admin: values.isAdmin === true,
          competition_setup: values.competitionSetup === true,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      const data = await res.json();
      message.success(`Created login for ${data.email}`);
      addForm.resetFields();
      setAddModal(false);
      loadUsers();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (uid: string, email: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${uid}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error);
      message.success(`Removed ${email}`);
      loadUsers();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    }
  };

  return (
    <>
      <Card
        title={<Space><SafetyCertificateOutlined /> Users</Space>}
        styles={{ body: { padding: isMobile ? 12 : 24, overflowX: 'auto' } }}
        extra={
          <Space size="small" wrap>
            <Tooltip title="Import all Firebase Auth accounts into this table">
              <Button icon={<SyncOutlined />} size="small" loading={syncing} onClick={handleSync}>
                {isMobile ? 'Sync' : 'Sync from Firebase'}
              </Button>
            </Tooltip>
            <Button icon={<ReloadOutlined />} size="small" onClick={loadUsers} loading={loading} />
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => setAddModal(true)}>
              {isMobile ? '' : 'Add'}
            </Button>
          </Space>
        }
      >
        {apiError && (
          <Alert
            type="error" showIcon message="Error" closable
            description={<Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{apiError}</Text>}
            style={{ marginBottom: 16 }} onClose={() => setApiError(null)}
          />
        )}
        <Alert
          type="info" showIcon
          message="Role changes take effect on the user's next login."
          style={{ marginBottom: 16 }}
        />
        <Spin spinning={loading}>
          {users.length === 0 && !loading && !apiError ? (
            <Empty
              description="No users yet ΓÇö click 'Sync from Firebase' to import all accounts"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={users}
              renderItem={(rec: UserRow) => (
                <List.Item
                  style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}
                  actions={[
                    <Popconfirm
                      key="remove"
                      title="Remove user?"
                      description="They will lose all roles on next re-login."
                      onConfirm={() => handleRemove(rec.uid, rec.email)}
                      okText="Remove" okButtonProps={{ danger: true }}
                      disabled={rec.uid === user?.uid}
                    >
                      <Tooltip title={rec.uid === user?.uid ? "Can't remove yourself" : 'Remove'}>
                        <Button danger icon={<DeleteOutlined />} size="small" disabled={rec.uid === user?.uid} />
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<UserOutlined style={{ fontSize: 20, color: 'var(--blue-bell)', marginTop: 2 }} />}
                    title={<Text strong style={{ fontSize: isMobile ? 13 : 14 }}>{rec.email}</Text>}
                    description={
                      <Space size={12} wrap style={{ marginTop: 4 }}>
                        <Space size={4}>
                          <CrownOutlined style={{ fontSize: 12, color: 'var(--black)' }} />
                          <Text style={{ fontSize: 12 }}>Admin</Text>
                          <Switch
                            size="small"
                            checked={rec.is_admin}
                            disabled={rec.uid === user?.uid}
                            onChange={(v) => handleUpdateRole(rec.uid, rec.email, v, rec.competition_setup)}
                          />
                        </Space>
                        <Space size={4}>
                          <ToolOutlined style={{ fontSize: 12, color: 'var(--black)' }} />
                          <Text style={{ fontSize: 12 }}>Live organiser</Text>
                          <Switch
                            size="small"
                            checked={rec.competition_setup}
                            onChange={(v) => handleUpdateRole(rec.uid, rec.email, rec.is_admin, v)}
                          />
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
        <Divider />
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag color="blue">Admin</Tag> Access to this panel and user management.
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag color="green">Live organiser</Tag> Can sign in to the FinishLynx desktop agent and call live organiser APIs (competition setup is in the agent, not the web app).
          </Text>
        </Space>
      </Card>

      <Modal
        title="Create User Login"
        open={addModal}
        onCancel={() => { setAddModal(false); addForm.resetFields(); }}
        footer={null}
        width={isMobile ? '95vw' : 440}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Create a Firebase login account and assign initial app roles. Share the password securely and ask the user to change it after first sign-in.
        </Paragraph>
        <Form form={addForm} layout="vertical" onFinish={handleAddUser}>
          <Form.Item name="email" label="Email" rules={[
            { required: true, message: 'Email is required' },
            { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' },
          ]}>
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="displayName" label="Display name">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Temporary password"
            rules={[
              { required: true, message: 'Please enter a temporary password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="At least 6 characters" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="isAdmin" label="Admin" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Switch />
          </Form.Item>
          <Form.Item name="competitionSetup" label="Live organiser" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={adding} block>Create User</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ClubOwnersTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [lastResponse, setLastResponse] = useState<any>(null);

  const handleCreate = async (values: any) => {
    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/owners`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          club_name: values.club_name,
          full_name: values.full_name || null,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to create club owner');
      setLastResponse(body);
      message.success(`Created club owner ${body.email}`);
      form.resetFields();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card
      title={<Space><TeamOutlined /> Club Owners</Space>}
      styles={{ body: { padding: isMobile ? 12 : 24 } }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Create a club owner account and trigger Firebase password setup email.
      </Paragraph>
      <Form form={form} layout="vertical" onFinish={handleCreate}>
        <Form.Item name="club_name" label="Club name" rules={[{ required: true }]}>
          <Input placeholder="ACME Athletics Club" />
        </Form.Item>
        <Form.Item name="full_name" label="Club owner full name">
          <Input placeholder="Optional" />
        </Form.Item>
        <Form.Item name="email" label="Club owner email" rules={[
              { required: true, message: 'Email is required' },
              { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' },
            ]}>
          <Input placeholder="owner@club.com" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={creating}>Create club owner</Button>
      </Form>
      {lastResponse && (
        <Alert
          style={{ marginTop: 16 }}
          type={lastResponse.invite_sent ? 'success' : 'warning'}
          showIcon
          message={`UID: ${lastResponse.uid}`}
          description={
            <Space direction="vertical" size={0}>
              <Text>Club: {lastResponse.club_name} (ID: {lastResponse.club_id})</Text>
              <Text>Email invite: {lastResponse.invite_sent ? 'sent' : `failed (${lastResponse.invite_message || 'unknown'})`}</Text>
            </Space>
          }
        />
      )}
    </Card>
  );
};

const AthleteAccountsTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [accounts, setAccounts] = useState<AthleteAccountRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [tagForm] = Form.useForm();

  const loadAthleteAccounts = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/athlete-accounts`, { headers });
      const text = await res.text();
      if (!res.ok) { setApiError(`${res.status}: ${text}`); return; }
      const data = JSON.parse(text);
      setAccounts(data.accounts || []);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAthleteAccounts(); }, [loadAthleteAccounts]);

  const openTagModal = (uid: string) => {
    setSelectedUid(uid);
    tagForm.resetFields();
    setTagModalOpen(true);
  };

  const handleTagAthlete = async (values: any) => {
    if (!selectedUid) return;
    setTagging(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/athlete-accounts/${selectedUid}/tag`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ athlete_id: Number(values.athlete_id) }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || 'Tag failed');
      message.success(`Tagged account to athlete ${payload.athlete_id}`);
      setTagModalOpen(false);
      setSelectedUid(null);
      loadAthleteAccounts();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setTagging(false);
    }
  };

  return (
    <>
      <Card
        title={<Space><SafetyCertificateOutlined /> Athlete Accounts</Space>}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
        extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadAthleteAccounts} loading={loading} />}
      >
        {apiError && (
          <Alert
            type="error"
            showIcon
            message="Error"
            description={apiError}
            closable
            style={{ marginBottom: 16 }}
            onClose={() => setApiError(null)}
          />
        )}
        <Spin spinning={loading}>
          {accounts.length === 0 && !loading && !apiError ? (
            <Empty description="No athlete accounts yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={accounts}
              renderItem={(acct: AthleteAccountRow) => (
                <List.Item
                  actions={[
                    <Button
                      key="tag"
                      type={acct.is_tagged ? 'default' : 'primary'}
                      size="small"
                      onClick={() => openTagModal(acct.uid)}
                    >
                      {acct.is_tagged ? 'Retag' : 'Tag athlete'}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<UserOutlined style={{ fontSize: 20, color: 'var(--blue-bell)', marginTop: 2 }} />}
                    title={
                      <Space wrap>
                        <Text strong>{acct.full_name || acct.email}</Text>
                        <Tag color={acct.is_tagged ? 'green' : 'gold'}>
                          {acct.is_tagged ? `Tagged: ${acct.athlete_id}` : 'Pending tag'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{acct.email}</Text>
                        <Text type="secondary">Club: {acct.club_name || 'Unassigned'}</Text>
                        <Text type="secondary">
                          DOB: {acct.date_of_birth ? dayjs(acct.date_of_birth).format('YYYY-MM-DD') : '—'}
                        </Text>
                        <Text type="secondary">Invite: {acct.invite_status || 'unknown'}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>
      <Modal
        title="Tag Athlete Account"
        open={tagModalOpen}
        onCancel={() => { setTagModalOpen(false); setSelectedUid(null); }}
        footer={null}
      >
        <Form form={tagForm} layout="vertical" onFinish={handleTagAthlete}>
          <Form.Item
            name="athlete_id"
            label="Athletes table ID (athletes_key)"
            rules={[{ required: true, message: 'Please enter athlete ID' }]}
          >
            <Input placeholder="e.g. 12345" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={tagging} block>
            Save link
          </Button>
        </Form>
      </Modal>
    </>
  );
};

const AthleteTagRequestsTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [requests, setRequests] = useState<AthleteTagRequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [tagForm] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/athlete-tag-requests`, { headers });
      const text = await res.text();
      if (!res.ok) { setApiError(`${res.status}: ${text}`); return; }
      const payload = JSON.parse(text);
      setRequests(payload.requests || []);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const approveOpen = (uid: string) => {
    setSelectedUid(uid);
    tagForm.resetFields();
    setModalOpen(true);
  };

  const approveRequest = async (values: any) => {
    if (!selectedUid) return;
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/athlete-tag-requests/${selectedUid}/approve`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ athlete_id: Number(values.athlete_id) }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || 'Approve failed');
      message.success('Tag request approved');
      setModalOpen(false);
      setSelectedUid(null);
      loadRequests();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const rejectRequest = async (uid: string) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/athlete-tag-requests/${uid}/reject`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || 'Reject failed');
      message.success('Tag request rejected');
      loadRequests();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    }
  };

  return (
    <>
      <Card
        title={<Space><SafetyCertificateOutlined /> Tag Requests</Space>}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
        extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadRequests} loading={loading} />}
      >
        {apiError && <Alert type="error" showIcon message={apiError} style={{ marginBottom: 16 }} />}
        <Spin spinning={loading}>
          {requests.length === 0 ? (
            <Empty description="No pending tag requests" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={requests}
              renderItem={(req: AthleteTagRequestRow) => (
                <List.Item
                  actions={[
                    <Button key="approve" type="primary" size="small" onClick={() => approveOpen(req.uid)}>Approve</Button>,
                    <Button key="reject" danger size="small" onClick={() => rejectRequest(req.uid)}>Reject</Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={<Text strong>{req.full_name || req.email || req.uid}</Text>}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{req.email || 'No email'}</Text>
                        <Text type="secondary">Club: {req.club_name || 'Unassigned'}</Text>
                        <Text type="secondary">DOB: {req.date_of_birth ? dayjs(req.date_of_birth).format('YYYY-MM-DD') : '—'}</Text>
                        <Text type="secondary">Note: {req.tag_request_note || 'None'}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>
      <Modal
        title="Approve Tag Request"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setSelectedUid(null); }}
        footer={null}
      >
        <Form form={tagForm} layout="vertical" onFinish={approveRequest}>
          <Form.Item name="athlete_id" label="Athletes table ID (athletes_key)" rules={[{ required: true }]}>
            <Input placeholder="e.g. 12345" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>Approve and tag</Button>
        </Form>
      </Modal>
    </>
  );
};

// ΓöÇΓöÇΓöÇ API Keys Tab ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

const ApiKeysTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/api-keys`, { headers });
      const text = await res.text();
      if (!res.ok) { setApiError(`${res.status}: ${text}`); return; }
      const data = JSON.parse(text);
      setKeys(data.keys || []);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleRevoke = async (id: number) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/api-keys/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error);
      message.success('Key revoked ΓÇö agent will lose access immediately.');
      loadKeys();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    }
  };

  return (
    <Card
      title={
        <Space>
          <KeyOutlined /> Agent API Keys
          {keys.length > 0 && <Badge count={keys.length} style={{ backgroundColor: 'var(--bright-sky)' }} />}
        </Space>
      }
      styles={{ body: { padding: isMobile ? 12 : 24, overflowX: 'auto' } }}
      extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadKeys} loading={loading} />}
    >
      {apiError && (
        <Alert
          type="error" showIcon message="Error" closable
          description={<Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{apiError}</Text>}
          style={{ marginBottom: 16 }} onClose={() => setApiError(null)}
        />
      )}
      <Alert
        type="warning" showIcon
        message="Revoking a key immediately stops that agent from uploading results."
        style={{ marginBottom: 16 }}
      />
      <Spin spinning={loading}>
        {keys.length === 0 && !loading && !apiError ? (
          <Empty description="No API keys yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={keys}
            renderItem={(rec: ApiKey) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="revoke"
                    title="Revoke this key?"
                    description="The agent using it will immediately lose access."
                    onConfirm={() => handleRevoke(rec.id)}
                    okText="Revoke" okButtonProps={{ danger: true }}
                  >
                    <Tooltip title="Revoke">
                      <Button danger icon={<StopOutlined />} size="small" />
                    </Tooltip>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={<KeyOutlined style={{ fontSize: 20, color: 'var(--blue-bell)', marginTop: 2 }} />}
                  title={
                    <Space>
                      <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>{rec.owner_email}</Text>
                      <Tag color="green" style={{ fontSize: 11 }}>Active</Tag>
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Created {new Date(rec.created_at).toLocaleString()}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Card>
  );
};

// ΓöÇΓöÇΓöÇ Main Admin Page ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

const Admin: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <div className="page-container">
      <Title level={isMobile ? 3 : 2}>Admin Panel</Title>
      <Paragraph type="secondary">
        Manage user roles, club ownership, athlete account tagging, athlete tag requests, API keys, and landing-page contact submissions.
      </Paragraph>
      <Tabs
        defaultActiveKey="users"
        size={isMobile ? 'small' : 'middle'}
        items={[
          {
            key: 'users',
            label: <Space><SafetyCertificateOutlined />{!isMobile && 'Users'}</Space>,
            children: <UsersTab />,
          },
          {
            key: 'clubowners',
            label: <Space><TeamOutlined />{!isMobile && 'Club Owners'}</Space>,
            children: <ClubOwnersTab />,
          },
          {
            key: 'athleteaccounts',
            label: <Space><UserOutlined />{!isMobile && 'Athlete Accounts'}</Space>,
            children: <AthleteAccountsTab />,
          },
          {
            key: 'athletetagrequests',
            label: <Space><SafetyCertificateOutlined />{!isMobile && 'Tag Requests'}</Space>,
            children: <AthleteTagRequestsTab />,
          },
          {
            key: 'apikeys',
            label: <Space><KeyOutlined />{!isMobile && 'API Keys'}</Space>,
            children: <ApiKeysTab />,
          },
          {
            key: 'scrapeurls',
            label: <Space><GlobalOutlined />{!isMobile && 'Scrape URLs'}</Space>,
            children: <ScrapeUrls />,
          },
          {
            key: 'contactsubmissions',
            label: <Space><MailOutlined />{!isMobile && 'Contact submissions'}</Space>,
            children: <ContactSubmissionsAdminTab />,
          },
        ]}
      />
    </div>
  );
};

export default Admin;
